import React from 'react';
import { Document, View, Text, StyleSheet } from '@react-pdf/renderer';

import {
  OfficialPage, DetailsBox, Signatures, OFFICIAL_BODY as BODY,
  pdfColors, pdfFontFamily, pdfLetter, pdfSpacing,
  fmtLongDate, fmtMoney, fmtAmountInWords,
  type SchoolBranding,
} from '../../../utils/pdf';

export interface ReceiptLine {
  /** Challan / fine reference, printed in mono. */
  ref?: string;
  description: string;
  amount: number;
}

type Pair = { label: string; value?: string | null };

/** Everything any receipt prints — fee, fine or other income map into this. */
export interface ReceiptData {
  /** "Fee Receipt", "Fine Receipt". */
  title: string;
  number: string;
  /** When the money came in (ISO). */
  receivedOn: string;
  /** Who it was paid for — in the letterhead. */
  payer: Pair[];
  /** How it was paid — set off beneath. */
  details: Pair[];
  refHeader: string;
  lines: ReceiptLine[];
  amount: number;
  currency: string;
  collectedBy?: string;
  /** Set when the payment has been reversed — the receipt says so. */
  reversed?: { at?: string | null; by?: string; reason?: string } | null;
}

const RULE = { borderBottomWidth: 0.6, borderBottomColor: pdfColors.textPrimary, borderBottomStyle: 'solid' } as const;
const HAIR = { borderBottomWidth: 0.5, borderBottomColor: pdfColors.divider, borderBottomStyle: 'solid' } as const;

const styles = StyleSheet.create({
  lead: { fontFamily: pdfFontFamily.serif, fontSize: BODY, lineHeight: 1.55, marginBottom: 12 },
  strong: { fontFamily: pdfFontFamily.serifBold },
  head: { flexDirection: 'row', paddingBottom: 4, ...RULE },
  th: { fontSize: 7, fontWeight: 600, textTransform: 'uppercase', letterSpacing: pdfLetter.openLabel, color: pdfColors.textSecondary },
  row: { flexDirection: 'row', paddingVertical: 6, ...HAIR },
  ref: { width: 92, fontFamily: pdfFontFamily.mono, fontSize: 8.5, paddingTop: 1.5 },
  for: { flex: 1, fontFamily: pdfFontFamily.serif, fontSize: BODY - 0.5, paddingRight: pdfSpacing.sm },
  amount: { width: 80, textAlign: 'right', fontFamily: pdfFontFamily.serif, fontSize: BODY - 0.5 },
  total: { flexDirection: 'row', alignItems: 'baseline', paddingVertical: 7, marginTop: 1, borderBottomWidth: 1.4, borderBottomColor: pdfColors.textPrimary, borderBottomStyle: 'solid' },
  totalLabel: { flex: 1, fontFamily: pdfFontFamily.serifBold, fontSize: BODY },
  totalValue: { fontFamily: pdfFontFamily.serifBold, fontSize: BODY + 2 },
  words: { fontFamily: pdfFontFamily.serifItalic, fontSize: BODY - 1, color: pdfColors.textSecondary, marginTop: 5 },
  reversed: { marginTop: pdfSpacing.md, fontFamily: pdfFontFamily.serifItalic, fontSize: BODY - 1, color: pdfColors.danger },
  signatures: { marginTop: pdfSpacing.xl },
});

const known = (pairs: Pair[]) => pairs.filter((p): p is { label: string; value: string } => !!p.value);

/**
 * The one receipt — fee or fine — on the school's official page (A5, half a
 * sheet): who paid, how, what for, the sum in figures and in words, and who
 * received it. A reversed payment's receipt is marked REVERSED and says why.
 */
const ReceiptDocument: React.FC<{ data: ReceiptData; branding: SchoolBranding; generatedAt: string }> = ({ data, branding, generatedAt }) => {
  const money = (n: number) => fmtMoney(n, data.currency);
  const details = known(data.details);
  return (
    <Document title={`${data.title} ${data.number}`} author={branding.name}>
      <OfficialPage
        branding={branding}
        size="A5"
        title={data.title}
        details={known(data.payer)}
        reference={{ label: 'Receipt No.', value: data.number }}
        date={fmtLongDate(data.receivedOn)}
        watermark={data.reversed ? 'REVERSED' : undefined}
        generatedAt={generatedAt}
      >
        <Text style={styles.lead}>
          Received with thanks the sum of <Text style={styles.strong}>{money(data.amount)}</Text> towards the following.
        </Text>

        {details.length > 0 && <DetailsBox rows={details.map(d => [d.label, d.value])} labelWidth={118} />}

        <View style={styles.head} wrap={false}>
          <Text style={[styles.th, { width: 92 }]}>{data.refHeader}</Text>
          <Text style={[styles.th, { flex: 1 }]}>For</Text>
          <Text style={[styles.th, { width: 80, textAlign: 'right' }]}>Amount</Text>
        </View>
        {data.lines.map((l, i) => (
          <View key={i} style={styles.row} wrap={false}>
            <Text style={styles.ref}>{l.ref || '—'}</Text>
            <Text style={styles.for}>{l.description || '—'}</Text>
            <Text style={styles.amount}>{money(l.amount)}</Text>
          </View>
        ))}
        <View wrap={false}>
          <View style={styles.total}>
            <Text style={styles.totalLabel}>Amount received</Text>
            <Text style={styles.totalValue}>{money(data.amount)}</Text>
          </View>
          <Text style={styles.words}>{fmtAmountInWords(data.amount, data.currency)}</Text>
        </View>

        {data.reversed && (
          <Text style={styles.reversed}>
            Reversed{data.reversed.at ? ` on ${fmtLongDate(data.reversed.at)}` : ''}
            {data.reversed.by ? ` by ${data.reversed.by}` : ''}
            {data.reversed.reason ? ` — ${data.reversed.reason}` : ''}. This receipt is no longer valid.
          </Text>
        )}

        <View style={styles.signatures}>
          <Signatures slots={[`Received by${data.collectedBy ? ` — ${data.collectedBy}` : ''}`]} inkSpace={26} />
        </View>
      </OfficialPage>
    </Document>
  );
};

export default ReceiptDocument;
